import React, { useState, useEffect, useCallback } from 'react';
import {
  PieChart, TrendingUp, AlertTriangle, Target, Sparkles, RefreshCcw, Loader2,
  DollarSign, Users, Megaphone, ArrowDown, ArrowUp, ExternalLink, Lightbulb,
  CheckCircle2, XCircle, AlertCircle, Activity, Calendar, Zap
} from 'lucide-react';
import { motion } from 'framer-motion';
import Sidebar from '../../components/Sidebar';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../utils/api';
import { toast } from 'sonner';

const SEMAFORO_STYLE = {
  verde:    { bg: 'bg-emerald-50',   border: 'border-emerald-300',  text: 'text-emerald-700',  dot: 'bg-emerald-500',  icon: CheckCircle2 },
  amarillo: { bg: 'bg-amber-50',     border: 'border-amber-300',    text: 'text-amber-700',    dot: 'bg-amber-500',    icon: AlertCircle },
  rojo:     { bg: 'bg-rose-50',      border: 'border-rose-300',     text: 'text-rose-700',     dot: 'bg-rose-500',     icon: XCircle }
};

const fmtCLP = (n) => (n == null ? '—' : '$' + (n).toLocaleString('es-CL', { maximumFractionDigits: 0 }));
const fmtPct = (n) => (n == null ? '—' : `${n}%`);

const Reportes = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [reporte, setReporte] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generandoIA, setGenerandoIA] = useState(false);
  const [dias, setDias] = useState(30);

  const cargar = useCallback(async (refresh = false) => {
    if (refresh) setGenerandoIA(true);
    else setLoading(true);
    try {
      const r = await api.get(`/reportes/ejecutivo?dias=${dias}&refresh_ia=${refresh}`, { timeout: 180000 });
      setReporte(r.data);
      if (refresh) toast.success('Análisis IA actualizado');
    } catch (e) {
      toast.error('Error cargando reporte');
    } finally {
      setLoading(false);
      setGenerandoIA(false);
    }
  }, [dias]);

  useEffect(() => { cargar(false); }, [cargar]);

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="reportes-page">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <div className="flex-1 p-8 overflow-y-auto max-h-screen">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          {/* Header */}
          <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <PieChart className="w-10 h-10 text-indigo-500" strokeWidth={1.5} />
              <div>
                <h1 className="text-4xl font-bold text-slate-900">Reporte Ejecutivo</h1>
                <p className="text-base text-slate-600 mt-1">
                  Marketing · Redes Sociales · Embudo Comercial · IA Director Estratégico
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Select value={String(dias)} onValueChange={(v) => setDias(parseInt(v))}>
                <SelectTrigger className="w-32" data-testid="select-dias">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 días</SelectItem>
                  <SelectItem value="14">14 días</SelectItem>
                  <SelectItem value="30">30 días</SelectItem>
                  <SelectItem value="60">60 días</SelectItem>
                  <SelectItem value="90">90 días</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => cargar(false)} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
                Recargar
              </Button>
              <Button
                onClick={() => cargar(true)}
                disabled={generandoIA}
                data-testid="btn-generar-ia"
                className="bg-indigo-600 hover:bg-indigo-700 gap-2"
              >
                {generandoIA ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Analizar con IA
              </Button>
            </div>
          </div>

          {loading && !reporte && (
            <div className="text-center py-20"><Loader2 className="w-10 h-10 animate-spin mx-auto text-slate-400" /></div>
          )}

          {reporte && (
            <>
              {/* INSIGHT CRÍTICO IA */}
              {reporte.ia?.insight_critico && (
                <Card className="p-6 mb-6 bg-gradient-to-br from-indigo-50 via-purple-50 to-rose-50 border-2 border-indigo-200">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-8 h-8 text-indigo-600 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <p className="text-xs uppercase tracking-wide text-indigo-700 font-semibold">Insight crítico del Director Estratégico IA</p>
                      <p className="text-xl font-bold text-slate-900 mt-1 leading-snug">{reporte.ia.insight_critico}</p>
                    </div>
                  </div>
                </Card>
              )}

              {/* SEMÁFOROS */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                {Object.entries(reporte.semaforos).map(([key, sem]) => {
                  const style = SEMAFORO_STYLE[sem.estado] || SEMAFORO_STYLE.amarillo;
                  const Icon = style.icon;
                  return (
                    <Card key={key} className={`p-4 ${style.bg} ${style.border} border-2`} data-testid={`semaforo-${key}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className={`w-4 h-4 ${style.text}`} />
                        <span className={`text-xs font-semibold uppercase ${style.text}`}>{sem.estado}</span>
                      </div>
                      <p className="text-xs text-slate-600">{sem.label}</p>
                      <p className={`text-2xl font-bold ${style.text} mt-1`}>
                        {typeof sem.valor === 'number' && sem.valor > 1000 ? sem.valor.toLocaleString('es-CL') : (typeof sem.valor === 'number' ? sem.valor : '—')}
                        {key === 'marketing_cpl' && typeof sem.valor === 'number' && <span className="text-xs ml-1">CLP</span>}
                        {(key === 'embudo_contacto' || key === 'embudo_conversion') && typeof sem.valor === 'number' && <span className="text-base ml-1">%</span>}
                      </p>
                    </Card>
                  );
                })}
              </div>

              {/* ROW 1: MARKETING + PREDICCIÓN */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <Card className="p-5 lg:col-span-2">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Megaphone className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold text-lg text-slate-900">Eficiencia de Inversión Marketing</h3>
                    </div>
                    <Badge variant="outline" className="text-xs">últimos {reporte.ventana_dias}d</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="p-3 rounded-lg bg-blue-50">
                      <p className="text-xs uppercase text-slate-600">Inversión</p>
                      <p className="text-2xl font-bold text-blue-700">{fmtCLP(reporte.marketing.inversion_total_clp)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-emerald-50">
                      <p className="text-xs uppercase text-slate-600">Leads</p>
                      <p className="text-2xl font-bold text-emerald-700">{reporte.marketing.leads_total}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-purple-50">
                      <p className="text-xs uppercase text-slate-600">CPL promedio</p>
                      <p className="text-2xl font-bold text-purple-700">{fmtCLP(reporte.marketing.cpl_promedio_clp)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {reporte.marketing.mejor_campana && (
                      <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50/30">
                        <div className="flex items-center gap-2 mb-1">
                          <ArrowUp className="w-4 h-4 text-emerald-600" />
                          <span className="text-xs font-semibold uppercase text-emerald-700">Mejor campaña (CPL más bajo)</span>
                        </div>
                        <p className="font-semibold text-slate-900">{reporte.marketing.mejor_campana.nombre}</p>
                        <p className="text-sm text-slate-600">{reporte.marketing.mejor_campana.leads_total} leads · CPL {fmtCLP(reporte.marketing.mejor_campana.cpl)}</p>
                      </div>
                    )}
                    {reporte.marketing.peor_campana && (
                      <div className="p-3 rounded-lg border border-amber-200 bg-amber-50/30">
                        <div className="flex items-center gap-2 mb-1">
                          <ArrowDown className="w-4 h-4 text-amber-600" />
                          <span className="text-xs font-semibold uppercase text-amber-700">Peor campaña (CPL más alto)</span>
                        </div>
                        <p className="font-semibold text-slate-900">{reporte.marketing.peor_campana.nombre}</p>
                        <p className="text-sm text-slate-600">{reporte.marketing.peor_campana.leads_total} leads · CPL {fmtCLP(reporte.marketing.peor_campana.cpl)}</p>
                      </div>
                    )}
                  </div>
                  {reporte.marketing.campanas_frias && reporte.marketing.campanas_frias.length > 0 && (
                    <div className="mt-3 p-3 rounded-lg bg-rose-50 border border-rose-200">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="w-4 h-4 text-rose-600" />
                        <span className="text-xs font-semibold uppercase text-rose-700">Campañas frías ({reporte.marketing.campanas_frias.length}) — gastaron sin generar leads</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {reporte.marketing.campanas_frias.slice(0, 5).map((c) => (
                          <Badge key={c.id} variant="outline" className="bg-white text-xs">
                            {c.nombre} · {fmtCLP(c.inversion_clp)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>

                {/* PREDICCIÓN */}
                <Card className="p-5 bg-gradient-to-br from-violet-50 to-fuchsia-50 border-violet-200">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-5 h-5 text-violet-600" />
                    <h3 className="font-semibold text-lg text-slate-900">Predicción cierre de mes</h3>
                  </div>
                  <div className="text-center mb-3">
                    <p className="text-xs text-slate-600 uppercase">Clientes proyectados</p>
                    <p className="text-5xl font-bold text-violet-700 my-2">{reporte.prediccion.clientes_proyectados_cierre_mes}</p>
                    <p className="text-xs text-slate-500">en los próximos {reporte.prediccion.dias_restantes_mes} días</p>
                  </div>
                  <div className="space-y-1 text-sm border-t border-violet-200 pt-3">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Velocidad de leads</span>
                      <span className="font-semibold">{reporte.prediccion.velocidad_leads_diaria}/día</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Leads proyectados</span>
                      <span className="font-semibold">+{reporte.prediccion.leads_proyectados_resto_mes}</span>
                    </div>
                  </div>
                </Card>
              </div>

              {/* ROW 2: EMBUDO + REDES */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                {/* EMBUDO */}
                <Card className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-5 h-5 text-orange-600" />
                    <h3 className="font-semibold text-lg text-slate-900">Diagnóstico del Embudo</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs uppercase text-slate-600">Total prospectos</p>
                      <p className="text-2xl font-bold">{reporte.embudo.total_prospectos}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-rose-50">
                      <p className="text-xs uppercase text-rose-700">Urgentes</p>
                      <p className="text-2xl font-bold text-rose-700">{reporte.embudo.urgentes}</p>
                    </div>
                  </div>
                  <div className="space-y-1 text-sm mb-3">
                    {Object.entries(reporte.embudo.por_estado || {}).map(([estado, n]) => {
                      const max = Math.max(...Object.values(reporte.embudo.por_estado || { x: 1 }));
                      const w = max > 0 ? (n / max) * 100 : 0;
                      return (
                        <div key={estado} className="flex items-center gap-2">
                          <span className="w-32 text-xs text-slate-600">{estado}</span>
                          <div className="flex-1 bg-slate-100 rounded-full h-5 relative overflow-hidden">
                            <div className="bg-orange-500 h-full transition-all" style={{ width: `${w}%` }} />
                            <span className="absolute inset-0 flex items-center justify-end pr-2 text-xs font-semibold text-slate-800">{n}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-t pt-3">
                    <p className="text-xs uppercase text-slate-500 mb-2">Distribución por pilar/tema</p>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(reporte.embudo.por_pilar || {}).slice(0, 6).map(([p, n]) => (
                        <Badge key={p} variant="outline" className="text-xs">{p}: {n}</Badge>
                      ))}
                    </div>
                  </div>
                  {reporte.embudo.cac_clp && (
                    <p className="text-sm text-slate-700 mt-3 p-2 bg-blue-50 rounded">
                      <span className="font-semibold">CAC actual:</span> {fmtCLP(reporte.embudo.cac_clp)} por cliente
                    </p>
                  )}
                </Card>

                {/* REDES */}
                <Card className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Activity className="w-5 h-5 text-rose-600" />
                    <h3 className="font-semibold text-lg text-slate-900">Redes Sociales (orgánico)</h3>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="p-2 rounded bg-blue-50 text-center">
                      <p className="text-xs text-slate-600">FB eng</p>
                      <p className="text-xl font-bold text-blue-700">{reporte.redes_sociales.engagement_facebook}</p>
                    </div>
                    <div className="p-2 rounded bg-rose-50 text-center">
                      <p className="text-xs text-slate-600">IG eng</p>
                      <p className="text-xl font-bold text-rose-700">{reporte.redes_sociales.engagement_instagram}</p>
                    </div>
                    <div className="p-2 rounded bg-slate-50 text-center">
                      <p className="text-xs text-slate-600">Alcance</p>
                      <p className="text-xl font-bold">{reporte.redes_sociales.reach_total.toLocaleString('es-CL')}</p>
                    </div>
                  </div>
                  <p className="text-xs uppercase text-slate-500 mb-2">Top 3 posts orgánicos</p>
                  <div className="space-y-2">
                    {(reporte.redes_sociales.top_3 || []).map((t, i) => (
                      <div key={i} className="p-2 rounded border bg-slate-50/50 text-xs">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className={`text-xs capitalize ${t.plataforma === 'instagram' ? 'bg-rose-50 text-rose-700' : 'bg-blue-50 text-blue-700'}`}>
                            {t.plataforma}
                          </Badge>
                          <span className="font-semibold">{t.engagement} eng</span>
                          <span className="text-slate-500">· {t.reach} reach</span>
                          {t.permalink_url && (
                            <a href={t.permalink_url} target="_blank" rel="noreferrer" className="ml-auto text-blue-600">
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        <p className="text-slate-700 line-clamp-2">{t.mensaje}</p>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              {/* ANÁLISIS IA */}
              {reporte.ia && Object.keys(reporte.ia).length > 0 && (
                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {reporte.ia.diagnostico_embudo && (
                      <Card className="p-5 border-orange-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Target className="w-5 h-5 text-orange-600" />
                          <h3 className="font-semibold text-slate-900">Diagnóstico Embudo</h3>
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed">{reporte.ia.diagnostico_embudo}</p>
                      </Card>
                    )}
                    {reporte.ia.correlacion_contenido && (
                      <Card className="p-5 border-rose-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Zap className="w-5 h-5 text-rose-600" />
                          <h3 className="font-semibold text-slate-900">Correlación Contenido ↔ Marketing</h3>
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed">{reporte.ia.correlacion_contenido}</p>
                      </Card>
                    )}
                  </div>

                  {(reporte.ia.acciones_marketing || reporte.ia.acciones_comercial) && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {reporte.ia.acciones_marketing && (
                        <Card className="p-5 bg-blue-50/50 border-blue-200">
                          <div className="flex items-center gap-2 mb-3">
                            <Megaphone className="w-5 h-5 text-blue-700" />
                            <h3 className="font-semibold text-blue-900">Acciones para Marketing</h3>
                          </div>
                          <ol className="space-y-2 list-decimal pl-5">
                            {reporte.ia.acciones_marketing.map((a, i) => (
                              <li key={i} className="text-sm text-slate-800 leading-relaxed">{a}</li>
                            ))}
                          </ol>
                          {reporte.ia.campana_a_recortar && (
                            <div className="mt-3 pt-3 border-t border-blue-200 text-sm">
                              <span className="text-rose-700">↓ Recortar: <b>{reporte.ia.campana_a_recortar}</b></span>
                              <br />
                              <span className="text-emerald-700">↑ Reforzar: <b>{reporte.ia.campana_a_reforzar}</b></span>
                            </div>
                          )}
                        </Card>
                      )}
                      {reporte.ia.acciones_comercial && (
                        <Card className="p-5 bg-orange-50/50 border-orange-200">
                          <div className="flex items-center gap-2 mb-3">
                            <Users className="w-5 h-5 text-orange-700" />
                            <h3 className="font-semibold text-orange-900">Acciones para Comercial</h3>
                          </div>
                          <ol className="space-y-2 list-decimal pl-5">
                            {reporte.ia.acciones_comercial.map((a, i) => (
                              <li key={i} className="text-sm text-slate-800 leading-relaxed">{a}</li>
                            ))}
                          </ol>
                          {reporte.ia.prioridad_contacto && (
                            <div className="mt-3 pt-3 border-t border-orange-200 text-sm text-orange-900">
                              <b>Prioridad de contacto:</b> {reporte.ia.prioridad_contacto}
                            </div>
                          )}
                        </Card>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Tabla campañas */}
              <Card className="p-5">
                <h3 className="font-semibold text-slate-900 mb-3">Todas las campañas</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-slate-500 text-xs uppercase border-b">
                      <tr>
                        <th className="text-left py-2 px-2">Campaña</th>
                        <th className="text-right py-2 px-2">Leads</th>
                        <th className="text-right py-2 px-2">Inversión</th>
                        <th className="text-right py-2 px-2">CPL</th>
                        <th className="text-right py-2 px-2">Clics</th>
                        <th className="text-center py-2 px-2">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(reporte.marketing.todas_campanas || []).map((c) => (
                        <tr key={c.id} className="border-b hover:bg-slate-50">
                          <td className="py-2 px-2 font-medium">{c.nombre}</td>
                          <td className="text-right">{c.leads_total}</td>
                          <td className="text-right">{fmtCLP(c.inversion_clp)}</td>
                          <td className="text-right">{c.cpl ? fmtCLP(c.cpl) : <span className="text-rose-600">∞</span>}</td>
                          <td className="text-right">{c.clics?.toLocaleString('es-CL') || 0}</td>
                          <td className="text-center">
                            <Badge variant="outline" className={`text-xs ${c.estado === 'Activa' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100'}`}>
                              {c.estado || '—'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              <p className="text-xs text-slate-400 mt-6 text-center">
                Reporte generado {new Date(reporte.generado_at).toLocaleString('es-CL')} ·
                Ventana de análisis: {reporte.ventana_dias} días
              </p>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Reportes;
